/**
 * View models of the import monitoring admin screens (TO-399). The views are
 * pure: the page hooks map the monitoring DTOs into these shapes (dates and
 * counts already formatted, `—` for a stage that has not run), pass a `t`
 * translate function and decide which downloads the viewer may use — a download
 * the viewer may not use arrives as an absent callback and is not rendered.
 */
import type { Option, Translate } from '../customer/customer-types'

export type { Option, Translate }

/** The eight counts of an execution, formatted (`—` when the stage has not run). */
export interface ImportCountsView {
	received: string
	valid: string
	rejected: string
	created: string
	updated: string
	unchanged: string
	deactivated: string
	planRejected: string
}

/** One executions-list row. */
export interface ImportExecutionRowView {
	id: string
	fileName: string
	logicalFileLabel: string
	transportLabel: string
	/** `key@version`. */
	definition: string
	receivedAt: string
	statusLabel: string
	counts: ImportCountsView
}

/** The drill-down header: file facts, status, counts and the validation outcome. */
export interface ImportExecutionSummaryView extends ImportExecutionRowView {
	failureCode: string
	sha256: string
	validationOutcomeLabel: string
	failedStageLabel: string
	totalErrors: string
}

/** One of the first validation errors (never a value). */
export interface ImportValidationErrorRowView {
	id: string
	recordNumber: string
	stageLabel: string
	code: string
	field: string
}

/** One change-log row; `diff` lists each changed field as `field: before → after`. */
export interface ImportChangeRowView {
	id: string
	seq: string
	sourceLine: string
	actionLabel: string
	outcomeLabel: string
	key: string
	diff: string
	errorCode: string
}

/** The eight count columns, in display order. */
export const IMPORT_COUNT_KEYS: readonly (keyof ImportCountsView)[] = [
	'received',
	'valid',
	'rejected',
	'created',
	'updated',
	'unchanged',
	'deactivated',
	'planRejected',
]
